import { HttpErrorResponse } from '@angular/common/http';

const MODERATION_ERROR_MARKERS = [
  'contenido contiene terminos no permitidos',
  'contenido contiene términos no permitidos',
  'terminos no permitidos',
  'términos no permitidos',
];

type TranslateFn = (key: string, params?: Record<string, string>) => string;

const FIELD_LABEL_KEYS: Record<string, string> = {
  address_text: 'MODERATION.ADDRESS',
  bio: 'MODERATION.BIOGRAPHY',
  body: 'MODERATION.BODY',
  caption: 'MODERATION.CAPTION',
  description: 'MODERATION.DESCRIPTION',
  display_name: 'MODERATION.PUBLIC_NAME',
  legal_name: 'MODERATION.LEGAL_NAME',
  location_name: 'MODERATION.LOCATION',
  management_contact: 'MODERATION.MANAGEMENT_CONTACT',
  opening_hours_text: 'MODERATION.OPENING_HOURS',
  phone: 'MODERATION.PHONE',
  social_name: 'MODERATION.SOCIAL_NAME',
  title: 'MODERATION.TITLE',
};

export function formatModerationAwareSaveError(
  error: unknown,
  fallbackMessage: string,
  translate: TranslateFn,
): string {
  const fields = extractModerationFields(error);
  if (fields === null) {
    return fallbackMessage;
  }

  if (fields.size === 0) {
    return translate('MODERATION.SAVE_BLOCKED_GENERIC');
  }

  const labels = [...fields].map((field) => translateFieldLabel(field, translate)).sort();
  if (labels.length === 1) {
    return translate('MODERATION.SAVE_BLOCKED_FIELD', { field: labels[0] });
  }

  return translate('MODERATION.SAVE_BLOCKED_FIELDS', { fields: labels.join(', ') });
}

function translateFieldLabel(field: string, translate: TranslateFn): string {
  const key = FIELD_LABEL_KEYS[field];
  if (!key) {
    return field.replace(/_/g, ' ');
  }

  const label = translate(key);
  return label && label !== key ? label : field.replace(/_/g, ' ');
}

function extractModerationFields(error: unknown): Set<string> | null {
  const root = error instanceof HttpErrorResponse ? error.error : error;
  const fields = new Set<string>();
  return collectModerationFields(root, fields) ? fields : null;
}

function collectModerationFields(value: unknown, fields: Set<string>, fieldName?: string): boolean {
  if (typeof value === 'string') {
    if (!isModerationMessage(value)) {
      return false;
    }
    if (fieldName) {
      fields.add(fieldName);
    }
    return true;
  }

  if (Array.isArray(value)) {
    return value.some((itemValue) => collectModerationFields(itemValue, fields, fieldName));
  }

  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  const source = record['source'];
  const objectFieldName =
    fieldNameFromJsonApiPointer(record['pointer'])
    ?? (source && typeof source === 'object'
      ? fieldNameFromJsonApiPointer((source as Record<string, unknown>)['pointer'])
      : undefined)
    ?? fieldName;

  let found = false;
  for (const [key, nestedValue] of Object.entries(record)) {
    const nextFieldName = key === 'pointer'
      ? fieldNameFromJsonApiPointer(nestedValue)
      : normalizedFieldName(key) ?? objectFieldName;
    found = collectModerationFields(nestedValue, fields, nextFieldName) || found;
  }
  return found;
}

function isModerationMessage(value: string): boolean {
  const normalized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
  return MODERATION_ERROR_MARKERS.some((marker) => normalized.includes(marker.normalize('NFD').replace(/[\u0300-\u036f]/g, '')));
}

function normalizedFieldName(key: string): string | null {
  if (key === 'errors' || key === 'error' || key === 'detail' || key === 'source') {
    return null;
  }
  return key.replace(/-/g, '_');
}

function fieldNameFromJsonApiPointer(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const match = value.match(/\/(?:attributes|relationships)\/([^/]+)$/);
  return match?.[1]?.replace(/-/g, '_');
}
